import { useState, useEffect, useMemo } from 'react';
import {
  OPPS_DATA,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_PERCENTAGE_TO_PLAN_OVERTIME,
  SPENDS_AND_OPPORTUNITIES__MONTHLY_PERCENTAGE_TO_PLAN_OVERTIME_LOCAL,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_PERCENTAGE_TO_PLAN_OVERTIME,
  SPENDS_AND_OPPORTUNITIES__WEEKLY_PERCENTAGE_TO_PLAN_OVERTIME_LOCAL,
  SPENDS_DATA,
  fiscalMonths,
} from '../constants';
import { getOTPayload, getSpendsAndOppsData } from '../service/SpendAndOppurtunitiesService';

export const usePercOverTimeDataHooks = (
  selectedStore,
  currentYear,
  viewType,
  userStores,
  dataType,
  spendToggleState
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [percOverTimeData, setPercOverTimeData] = useState({
    xAxisData: [],
    series: [],
    legend: [],
  });
  const [selectedYears, setSelectedYears] = useState([currentYear]);
  const [selectedComparisonStore, setSelectedComparisonStore] = useState('');

  const branchOptions = useMemo(() => {
    const stores = userStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    return stores.sort((a, b) => a.value - b.value);
  }, [userStores]);

  let compareWith = [
    { label: 'None', value: 'None' },
    { label: 'GALE Markets', value: 'GALE Markets' },
    { label: 'Store Region', value: 'Store Region' },
  ];
  if (branchOptions.find((store) => store.value === selectedStore)?.label === 'GALE Markets') {
    compareWith = [
      { label: 'None', value: 'None' },
      { label: 'Store Region', value: 'Store Region' },
    ];
  }

  const handleYearChange = (newYear) => {
    setSelectedYears(newYear);
  };

  const handleOOTStoreChange = (newStore) => {
    setSelectedComparisonStore(newStore);
  };

  const transformData = (rawData, viewType) => {
    const sortedData = rawData.sort((a, b) =>
      viewType === 'Monthly' ? a.fiscal_month - b.fiscal_month : a.fiscal_week - b.fiscal_week
    );
    const xAxisData = [
      ...new Set(
        sortedData.map((item) =>
          viewType === 'Monthly' ? fiscalMonths[item.fiscal_month] : `W${item.fiscal_week}`
        )
      ),
    ];
    let hasRegionID = false;
    let series = [];
    let legend = new Set();

    selectedYears.forEach((year) => {
      const yearData = sortedData.filter((data) => data.fiscal_year === year);
      series.push({
        name: `${year} - ${branchOptions.find((store) => store.value === selectedStore)?.label}`,
        type: 'line',
        data: yearData.map((item) =>
          dataType === OPPS_DATA ? item.opps_to_plan?.toFixed(2) : item.spend_to_plan?.toFixed(2)
        ),
      });
      legend.add(
        `${year} - ${branchOptions.find((store) => store.value === selectedStore)?.label}`
      );
    });
    if (branchOptions.filter((store) => store?.value === selectedStore)?.[0]?.region) {
      hasRegionID = true;
    }
    if (
      (selectedComparisonStore === 'GALE Markets' || selectedComparisonStore === 'Store Region') &&
      hasRegionID
    ) {
      const nameSuffix =
        selectedComparisonStore === 'GALE Markets'
          ? 'GALE Markets Average'
          : 'Store Region Average';
      // Determine the data values based on comparison type and data type
      const getData = (item) => {
        if (selectedComparisonStore === 'GALE Markets') {
          return dataType === SPENDS_DATA
            ? item.gale_spend_to_plan?.toFixed(2)
            : item.gale_opps_to_plan?.toFixed(2);
        } else {
          return dataType === SPENDS_DATA
            ? item.region_spend_to_plan?.toFixed(2)
            : item.region_opps_to_plan?.toFixed(2);
        }
      };

      series.push({
        name: nameSuffix,
        type: 'line',
        data: sortedData.map((item) => getData(item)),
      });
      legend.add(nameSuffix);
    }

    legend = Array.from(legend);
    setPercOverTimeData({ xAxisData, series, legend });
  };

  const fetchData = async () => {
    setIsLoading(true);
    const monthlyKey = !spendToggleState
      ? SPENDS_AND_OPPORTUNITIES__MONTHLY_PERCENTAGE_TO_PLAN_OVERTIME
      : SPENDS_AND_OPPORTUNITIES__MONTHLY_PERCENTAGE_TO_PLAN_OVERTIME_LOCAL;
    const weeklyKey = !spendToggleState
      ? SPENDS_AND_OPPORTUNITIES__WEEKLY_PERCENTAGE_TO_PLAN_OVERTIME
      : SPENDS_AND_OPPORTUNITIES__WEEKLY_PERCENTAGE_TO_PLAN_OVERTIME_LOCAL;
    const queryKey = viewType === 'Monthly' ? monthlyKey : weeklyKey;
    const payload = getOTPayload(queryKey, selectedStore, selectedYears);

    try {
      const response = await getSpendsAndOppsData(payload, queryKey, dataType);
      if (response.success) {
        transformData(response.data[queryKey].result, viewType);
      } else {
        throw new Error('Data fetch was successful but data structure was unexpected.');
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPercOverTimeData({
        xAxisData: [],
        series: [],
        legend: [],
      });
      if (selectedYears.length > 0 && selectedStore !== undefined && viewType !== undefined) {
        fetchData();
      }
    }, 1000); // 1000ms delay

    // Cleanup function to clear the timeout if the value changes before the delay is over
    return () => clearTimeout(timer);
  }, [selectedStore, selectedYears, viewType, selectedComparisonStore, spendToggleState]);

  return {
    selectedYears,
    isLoading,
    error,
    percOverTimeData,
    handleYearChange,
    handleOOTStoreChange,
    selectedComparisonStore,
    compareWith,
  };
};
